// Wishlist, after Louis Vuitton: hearts on products, a header heart with a dot when the list has
// items, a toast on add/remove, and a wishlist page (sections/main-wishlist.liquid).
//
// The list lives in this browser's localStorage. Signed-in customers get their own list (keyed by
// customer id); anything saved while signed out is merged into it on sign-in. Themes can't write
// to customer accounts, so lists don't follow a customer to another device.
(() => {
  if (window.CRWishlist) return;

  const config = window.wishlistConfig || {};
  const GUEST_KEY = 'cr-wishlist';
  const RECENT_KEY = 'cr-recently-viewed';
  const key = config.customerId ? `${GUEST_KEY}:customer:${config.customerId}` : GUEST_KEY;

  const read = (k) => {
    try {
      const value = JSON.parse(localStorage.getItem(k) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch (error) {
      return [];
    }
  };
  const write = (k, list) => {
    try {
      localStorage.setItem(k, JSON.stringify(list));
    } catch (error) {
      // Storage unavailable (private mode, blocked); the list just won't persist.
    }
  };

  // Signed in with a guest list on this browser: merge it into the account list once.
  if (config.customerId) {
    const guest = read(GUEST_KEY);
    if (guest.length) {
      write(key, [...new Set([...guest, ...read(key)])]);
      write(GUEST_KEY, []);
    }
  }

  const listeners = new Set();
  const emit = () => listeners.forEach((fn) => fn(store.items()));
  window.addEventListener('storage', (event) => event.key === key && emit());

  const store = {
    items: () => read(key),
    has: (handle) => read(key).includes(handle),
    add(handle) {
      const list = read(key).filter((h) => h !== handle);
      list.unshift(handle);
      write(key, list);
      emit();
    },
    remove(handle) {
      write(key, read(key).filter((h) => h !== handle));
      emit();
    },
    toggle(handle) {
      store.has(handle) ? store.remove(handle) : store.add(handle);
      return store.has(handle);
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    recent: () => read(RECENT_KEY),
    recordView(handle) {
      write(RECENT_KEY, [handle, ...read(RECENT_KEY).filter((h) => h !== handle)].slice(0, 8));
    },
  };
  window.CRWishlist = store;

  const productCache = new Map();
  store.product = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  // Toast: "The item X has been added to your wishlist / Access your wishlist"
  let toastTimer;
  store.toast = async (handle, added) => {
    let toast = document.querySelector('.wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'wishlist-toast';
      toast.innerHTML = `
        <div class="wishlist-toast__overlay"></div>
        <div class="wishlist-toast__panel" role="status" aria-live="polite">
          <div class="wishlist-toast__media"></div>
          <p class="wishlist-toast__text"></p>
          <button type="button" class="wishlist-toast__close" aria-label="${config.closeLabel || 'Close'}">
            <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="1.2"/></svg>
          </button>
        </div>`;
      document.body.appendChild(toast);
      const hide = () => toast.classList.remove('is-visible');
      toast.querySelector('.wishlist-toast__overlay').addEventListener('click', hide);
      toast.querySelector('.wishlist-toast__close').addEventListener('click', hide);
      document.addEventListener('keyup', (event) => event.key === 'Escape' && hide());
    }

    const product = await store.product(handle);
    const title = product ? product.title : handle;
    const media = toast.querySelector('.wishlist-toast__media');
    const image = product && (product.featured_image || product.images?.[0]);
    media.innerHTML = added && image ? `<img src="${image}${image.includes('?') ? '&' : '?'}width=160" alt="">` : '';
    media.hidden = !(added && image);

    const text = toast.querySelector('.wishlist-toast__text');
    text.textContent = '';
    text.append(`The item ${title} has been ${added ? 'added to' : 'removed from'} your wishlist`);
    if (added) {
      const link = document.createElement('a');
      link.href = config.pageUrl || '/search?view=wishlist';
      link.textContent = 'Access your wishlist';
      text.append(document.createElement('br'), link);
    }

    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4500);
  };

  // <wishlist-button data-handle="..."> : heart that toggles a product
  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.handle = this.dataset.handle;
          this.button = this.querySelector('button');
          if (!this.handle || !this.button) return;
          this.button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const added = store.toggle(this.handle);
            if (this.dataset.toast !== 'false') store.toast(this.handle, added);
          });
          this.unsubscribe = store.subscribe(() => this.render());
          this.render();
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        render() {
          const active = store.has(this.handle);
          this.classList.toggle('is-active', active);
          this.button.setAttribute('aria-pressed', active);
          this.button.setAttribute('aria-label', `${active ? 'Remove from' : 'Add to'} wishlist: ${this.dataset.title || ''}`.trim());
        }
      }
    );
  }

  // <wishlist-link> : header heart; shows a dot while the list has items
  if (!customElements.get('wishlist-link')) {
    customElements.define(
      'wishlist-link',
      class WishlistLink extends HTMLElement {
        connectedCallback() {
          this.unsubscribe = store.subscribe(() => this.render());
          this.render();
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        render() {
          const count = store.items().length;
          this.toggleAttribute('data-has-items', count > 0);
          const label = this.querySelector('.visually-hidden');
          if (label) label.textContent = count ? `Wishlist (${count} items)` : 'Wishlist';
        }
      }
    );
  }
})();
